import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { startCase } from 'lodash';
import {
  FieldMappingMetadata,
  ProjectStackField,
} from '~/src/entities/project/types';
import {
  FieldMappingResponse,
  useIsValueApplied,
} from '~/src/entities/fieldMapping';
import { useFieldMappingContext } from '~/src/pages/Populate/FieldMappingProvider';
import FieldMappingWidget from '../FieldMappingWidget/FieldMappingWidget';

export type FieldMappingBadgeProps = {
  metadata?: FieldMappingMetadata;
  hasDeletedFieldMapping?: boolean;
  onOpen: () => void;
  onClose: () => void;
  onMappingCreated?: (response?: FieldMappingResponse) => void;
  isOpen: boolean;
  projectField: ProjectStackField;
  isRoleCard: boolean;
  contactId?: number;
  isMappingDeleted?: boolean;
};

export const FieldMappingBadge: React.FC<FieldMappingBadgeProps> = ({
  metadata,
  hasDeletedFieldMapping = false,
  onOpen,
  onClose,
  onMappingCreated,
  isOpen,
  projectField,
  isRoleCard,
  contactId,
  isMappingDeleted = false,
}) => {
  const { getFilteredOptions } = useFieldMappingContext();

  const hasOptions = useMemo(() => {
    const options = getFilteredOptions(
      projectField.variable_type,
      isRoleCard,
      contactId,
    );
    return options.length > 0;
  }, [getFilteredOptions, projectField.variable_type, isRoleCard, contactId]);

  const isValueApplied = useIsValueApplied({
    metadata,
    fieldId: projectField.fid,
    variableType: projectField.variable_type,
    isRoleCard,
    contactId,
  });

  const getBadgeText = () => {
    if (!hasOptions) {
      return 'No matching Clio Manage fields available.';
    }

    if (isMappingDeleted || hasDeletedFieldMapping) {
      return '';
    }

    const sourceField = metadata?.source_field;
    if (sourceField) {
      if (!isValueApplied) return '';
      const attributeName = sourceField.split('.').at(-1) ?? '';
      const displayEntity = isRoleCard ? 'Contact' : 'Matter';
      return `Clio Manage > ${displayEntity} > ${startCase(attributeName)}`;
    }

    return 'No data found.';
  };

  const badgeText = getBadgeText();
  const hasActiveMapping = !isMappingDeleted && !!metadata?.source_field;
  const initialValue = isMappingDeleted ? '' : metadata?.source_field ?? '';
  const showAsLinked = hasActiveMapping && isValueApplied;

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 0.5,
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={(theme) => ({
          color: theme.palette.grey[600],
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        })}
      >
        <Box
          component="span"
          sx={(theme) => ({
            verticalAlign: 'middle',
            marginRight: 0.5,
            display: 'inline-flex',
            '& svg': {
              width: 14,
              height: 14,
              color: theme.palette.grey[600],
            },
          })}
        >
          {showAsLinked ? <LinkIcon /> : <LinkOffIcon />}
        </Box>
        {badgeText}
        {hasOptions && (
          <>
            {' '}
            <Box
              component="span"
              onClick={onOpen}
              sx={(theme) => ({
                color: theme.palette.blue[600],
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              })}
            >
              {showAsLinked ? 'Change' : 'Use Clio Manage data'}
            </Box>
          </>
        )}
      </Typography>
      {isOpen && hasOptions && (
        <FieldMappingWidget
          onClose={onClose}
          onMappingCreated={onMappingCreated}
          initialValue={initialValue}
          fieldId={projectField.fid}
          isRoleCard={isRoleCard}
          fieldMappingId={
            isMappingDeleted ? undefined : metadata?.field_mapping_id
          }
          contactId={contactId}
        />
      )}
    </Box>
  );
};
